import Button from "../../button/Button";
import styles from "./SurveyModal.module.scss";
import close from "../../../assets/icons/close.svg";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EVENT_NAMES } from "../../../types/event-names";
import { trackEvent } from "../../../utils/analytics";
import { useSurveyMutation } from "../../../store/services/details-api";
import { useAppSelector } from "../../../store/hooks";
import shuffleArray from "../../../utils/shuffleArray";

interface SurveyModalProps {
  callbackAction: (action: "deactivate" | "close" | "back" | "offer") => void;
}

type Reason = {
  key: string;
  defaultLabel: string;
  hasDetails?: boolean;
};

const SurveyModal: React.FC<SurveyModalProps> = ({ callbackAction }) => {
  const { t } = useTranslation();
  const [survey] = useSurveyMutation();
  const [isLoading, setIsLoading] = useState(false);
  const userDetails = useAppSelector((state) => state.auth.user);

  const reasons: Reason[] = useMemo(() => {
    const baseReasons: Reason[] = [
      { key: "didnt_know_subscribed", defaultLabel: "I didn't know I subscribed" },
      { key: "control_over_payments", defaultLabel: "I want more control over payments" },
      { key: "too_expensive", defaultLabel: "It's too expensive" },
      { key: "dont_use_device", defaultLabel: "I don't use the device I bought it for" },
      { key: "caused_issues", defaultLabel: "The product caused issues on my device" },
      { key: "install_activate_problem", defaultLabel: "I couldn't download, install, or activate" },
      { key: "competitor", defaultLabel: "I decided on a competitor's product" },
      { key: "duplicate_purchase", defaultLabel: "I bought the same product twice" },
      { key: "didnt_meet_expectations", defaultLabel: "The product doesn't work as I expected" },
    ];

    const other: Reason = { key: "other", defaultLabel: "Other", hasDetails: false };

    const shuffled = shuffleArray(baseReasons);

    return [...shuffled, other];
  }, []);

  const [selectedReason, setSelectedReason] = useState<string>("");
  const [otherText, setOtherText] = useState<string>("");

  const selected = reasons.find((r) => r.key === selectedReason);
  const isOther = selected?.hasDetails === true;
  const canContinue = Boolean(selectedReason) && (!isOther || otherText.trim().length > 0);

  useEffect(() => {
    trackEvent(EVENT_NAMES.SURVEY_OPENED, {});
  }, []);

  // Handle continue
  const handleContinue = async () => {
    if (!canContinue) return;

    setIsLoading(true);

    // Send survey data
    await survey({
      email: userDetails?.email as string,
      user_id: userDetails?.id as string,
      reason: selected?.key || "",
      survey: 1,
      reasonId: selected?.key || "",
    });

    // Analytics
    trackEvent(EVENT_NAMES.SURVEY_SUBMITTED, {
      reason_key: selected?.key || "",
      reason_label: t(selected?.defaultLabel || ""),
      has_details: isOther,
    });

    // Your existing flow likely proceeds to actual cancellation:
    if(selected?.key === "too_expensive") {
      callbackAction("offer");
    } else {
      callbackAction("deactivate");
    }

    setIsLoading(false);
  };

  // Handle back
  const handleBack = () => {
    callbackAction("back");
    trackEvent(EVENT_NAMES.SURVEY_BACK);
  };

  // Handle close
  const handleClose = () => {
    callbackAction("close");
    trackEvent(EVENT_NAMES.SURVEY_CANCELLED, {});
  };

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-labelledby="survey-title">
      <div className={styles.root_header}>
        <p id="survey-title" className={styles.root_header_title}>
          {t("Please tell us why you're cancelling")}
        </p>
        <div className={styles.root_header_img} onClick={handleClose} role="button" aria-label={t("Close")}>
          <img src={close} alt={t("Close") || "close"} />
        </div>
      </div>

      <div
        className={styles.root_body}
        role="radiogroup"
        aria-label={t("Cancellation reasons") || "Cancellation reasons"}
      >
        {reasons.map((r) => {
          const label = t(r.defaultLabel);
          const id = `reason-${r.key}`;
          return (
            <label key={r.key} htmlFor={id} className={styles.root_body_reasonItem}>
              <input
                id={id}
                type="radio"
                name="cancel-reason"
                value={r.key}
                checked={selectedReason === r.key}
                onChange={() => setSelectedReason(r.key)}
                className={styles.root_body_reasonRadio}
              />
              <span className={styles.root_body_reasonLabel}>{label}</span>
              {r.hasDetails && selectedReason === r.key && (
                <textarea
                  className={styles.root_body_reasonDetails}
                  placeholder={t("Please share a few details (optional)") || "Please share a few details (optional)"}
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  rows={3}
                />
              )}
            </label>
          );
        })}
      </div>

      <div className={styles.root_footer}>
        <Button
          onClick={handleContinue}
          text={t("Continue")}
          classNames={`${styles.root_footer_pause} ${!canContinue ? styles.root_footer_isDisabled : ""}`}
          disabled={!canContinue}
          type={!canContinue ? "disabled" : "primary"}
          isLoader={isLoading}
        />
        <Button onClick={handleBack} text={t("Go Back")} type="secondary" classNames={styles.root_footer_cancel} />
      </div>
    </div>
  );
};

export default SurveyModal;
